import { useEffect, useState } from "react";
import ReactGA from 'react-ga4';
import {
    Elements,
    PaymentElement,
    useElements,
    useStripe
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useLocation, useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import './checkout.scss';
import {
    blue300,
    blue500,
    gray010,
    gray300,
    gray600,
    gray800
} from "../../uiKit/assets/colors/colors";
import { BankCard } from "../../uiKit/assets/svg/icons";
import CustomButton from "../../uiKit/Button/CustomButton";
import Title from "../../uiKit/Title/Title";
import Loading from "../../uiKit/Loading/loading";
import CheckoutInfo from "./checkoutInfo";
import { FUND_INFO } from "../../helpers/constants";

import { setAllThankOpen, setCampaignThankOpen, setCommunityThankOpen, setStripeOpen, setStripeOpenDonateAll } from "../../redux/state/donationSlice";
import { campaignPaymentIntent, ourBalancePaymentIntent } from "../../redux/state/paymentSlice";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY, { locale: 'en' });

const CheckoutForm = ({ type }) => {
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const stripe = useStripe();
    const elements = useElements();

    const [isPaymentElementReady, setIsPaymentElementReady] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const closeStripeHandler = () => {
        dispatch(setStripeOpen(false));
        dispatch(setStripeOpenDonateAll(false))
    };

    const payWithNewCard = async () => {

        if (!stripe || !elements) {
            return;
        }

        ReactGA.event(`donate_${pathname.split('/')[1].replace('-', '_')}_new_card_checkout`);

        setIsLoading(true);
        const result = await stripe.confirmPayment({
            elements,
            redirect: 'if_required',
        })

        if (result.paymentIntent?.status === "succeeded") {
            setIsLoading(false);
            closeStripeHandler();
            if (type === 0) dispatch(setCampaignThankOpen(true));
            if (type === 1) dispatch(setCommunityThankOpen(true));
            if (type === 2) dispatch(setAllThankOpen(true));
        }

        if (result.error) {
            setIsLoading(false);
            toast.info(result.error.message);
        }

    }


    return (
        <>
            <div style={{ marginTop: '16px' }}>
                <PaymentElement
                    options={{
                        readOnly: isLoading
                    }}
                    onReady={() => setIsPaymentElementReady(true)}
                />
            </div>
            <CheckoutInfo />
            <CustomButton
                title='Donate'
                onClick={payWithNewCard}
                style={{ marginTop: '16px' }}
                disabled={!stripe || !isPaymentElementReady || isLoading}
                loading={isLoading}
            />
            <CustomButton
                title={'Decline'}
                style={{ marginTop: '12px' }}
                isWhite
                onClick={closeStripeHandler}
            />
            <Title
                text={FUND_INFO}
                fontSize={12}
                lineHeight={14}
                overflowText={false}
                fontWeight={400}
                style={{
                    marginTop: "16px",
                    marginBottom: "40px",
                }}
                color={gray600}
            />
        </>
    )
}

const Checkout = ({ Component, type }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { communityId, campaignId } = useParams();

    const { search } = useLocation();
    const query = new URLSearchParams(search);
    const paramsCommunityId = query.get("communityId");
    const paramsCampaignId = query.get("campaignId");

    const body = useSelector(state => state.donation.body, shallowEqual);
    const clientSecret = useSelector(state => state.payment.paymentInfo.clientSecret, shallowEqual);
    const savedCard = useSelector(state => state.payment.savedCard, shallowEqual);
    const error = useSelector(state => state.payment.error, shallowEqual);
    const isLoadingOurBalance = useSelector(state => state.payment.isLoading.ourBalancePaymentIntent, shallowEqual);
    const isLoadingCampaign = useSelector(state => state.payment.isLoading.campaignPaymentIntent, shallowEqual);
    const isLoadingDonateAll = useSelector(state => state.payment.isLoading.donateAllPaymentIntent, shallowEqual);

    const [useSavedCard, setUseSavedCard] = useState(false);

    const closeStripeHandler = () => {
        dispatch(setStripeOpen(false));
        dispatch(setStripeOpenDonateAll(false));
    };

    useEffect(() => {
        if (!!error?.errors?.Amount) {
            toast.info(error.errors.Amount[0] ?? 'something went wrong');
        }
    }, [error, history])

    useEffect(() => {
        if (!!savedCard && !!savedCard.id) {
            setUseSavedCard(true);
        }
    }, [savedCard, dispatch])

    useEffect(() => {
        if (useSavedCard) return;
        const idCommunity = !!paramsCommunityId ? paramsCommunityId : communityId;
        if (type === 0 && !!idCommunity && !!body.incomingBalanceId) {
            dispatch(campaignPaymentIntent({
                body,
                communityId: idCommunity,
                campaignId: body.incomingBalanceId,
            }))
        }
        if (type === 1 && !!communityId) {
            dispatch(ourBalancePaymentIntent({
                body,
                communityId,
            }))
        }
    }, [
        useSavedCard,
        body,
        campaignId,
        communityId,
        dispatch,
        type,
        paramsCommunityId,
        paramsCampaignId,
    ]);

    return (
        <div style={{ flexDirection: 'column', zIndex: 1000 }}>
            <div className="formWrapper" >
                {
                    !!savedCard && !!savedCard.id && (
                        <div style={{ display: 'flex', width: '100%' }}>
                            <div
                                style={{
                                    width: '174px',
                                    height: '78px',
                                    border: `1px solid ${useSavedCard ? blue300 : gray300}`,
                                    borderRadius: '10px',
                                    marginRight: '16px',
                                    padding: '16px',
                                    backgroundColor: gray010,
                                    cursor: 'pointer'
                                }}
                                onClick={() => setUseSavedCard(true)}
                            >
                                <BankCard disabled={useSavedCard} />
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Title
                                        text='Card'
                                        fontWeight={500}
                                        color={`${useSavedCard ? blue500 : gray800}`}
                                    />
                                    <Title
                                        text={savedCard.last4}
                                        fontWeight={500}
                                        color={`${useSavedCard ? blue500 : gray800}`}
                                    />
                                </div>
                            </div>
                            <div
                                style={{
                                    width: '174px',
                                    height: '78px',
                                    border: `1px solid ${!useSavedCard ? blue300 : gray300}`,
                                    borderRadius: '10px',
                                    padding: '16px',
                                    backgroundColor: gray010,
                                    cursor: 'pointer'
                                }}
                                onClick={() => setUseSavedCard(false)}
                            >
                                <BankCard disabled={!useSavedCard} />
                                <Title
                                    text='New card'
                                    fontWeight={500}
                                    color={`${!useSavedCard ? blue500 : gray800}`}
                                />
                            </div>
                        </div>
                    )
                }
                {
                    !!clientSecret && !useSavedCard && (
                        <Elements stripe={stripePromise} options={{ clientSecret }}>
                            <CheckoutForm type={type} />
                        </Elements>
                    )
                }
                {
                    useSavedCard && (
                        <>
                            <CheckoutInfo />
                            <Component />
                            <CustomButton
                                title={'Decline'}
                                style={{ marginTop: '12px' }}
                                isWhite
                                onClick={closeStripeHandler}
                            />
                            <Title
                                text={FUND_INFO}
                                fontSize={12}
                                lineHeight={14}
                                overflowText={false}
                                fontWeight={400}
                                style={{
                                    marginTop: "16px",
                                    marginBottom: "40px",
                                }}
                                color={gray600}
                            />
                        </>
                    )
                }
                {
                    (isLoadingOurBalance || isLoadingCampaign || isLoadingDonateAll) && <Loading />
                }
            </div>
        </div>
    )
}

export default Checkout;